import { useSceneStore } from '../store/useSceneStore';
import { useManifest } from '../content/useManifest';
import { useSourcesStore, type SourceRecord } from '../store/useSourcesStore';

/**
 * SourcesPanel — right-hand slide-in aside that surfaces paradata for the
 * currently-selected manifest object.
 *
 * Plan 04-03 Task 3 (PARA-03).
 *
 * Chrome: period-quiet serif (Goudy Old Style) on cream #FDF6EE with
 * espresso #3B2F2F text — AESTHETIC-BRIEF Rule 7 compliance. No glass
 * morphism, no rounded cards, no pronounced shadows; underlying rule is
 * "painted paper surface, not modern flat design."
 *
 * Resolution chain (all reads are synchronous since sources are loaded once
 * at App root via `useSources` hook in Task 4):
 *   1. selectedObjectId from useSceneStore
 *   2. manifest.objects.find(o => o.id === selectedObjectId)
 *   3. obj.sourceIds.map(id => sourcesStore.records.get(id)) with graceful
 *      filtering of unknown ids (Pattern 4 — panel never crashes on
 *      stale/orphan sourceId references)
 *
 * Empty resolution (unsourced object, or every sourceId was orphaned) renders
 * a Bhamwiki-voice fallback message pointing forward at Phase 6.
 */

const TIER_COLORS: Record<string, string> = {
  documented: '#6B7C4E',      // sage — full saturation
  inferred: '#B8602C',        // clay
  'stand-in': '#8B8077',      // muted gray
  background_context: '#3B2F2F',
};

const SERIF_STACK =
  '"Goudy Old Style", "Cormorant Garamond", Georgia, serif';

const CREAM = '#FDF6EE';
const ESPRESSO = '#3B2F2F';

export function SourcesPanel() {
  const selected = useSceneStore((s) => s.selectedObjectId);
  const setSelected = useSceneStore((s) => s.setSelectedObjectId);
  const { manifest } = useManifest();
  const records = useSourcesStore((s) => s.records);
  const sourcesLoaded = useSourcesStore((s) => s.loaded);

  if (!selected) return null;
  if (!manifest) return null;
  if (!sourcesLoaded) return null;

  const obj = manifest.objects.find((o) => o.id === selected);
  if (!obj) return null;

  const resolved: SourceRecord[] = obj.sourceIds
    .map((id) => records.get(id))
    .filter((r): r is SourceRecord => r !== undefined);

  const tierColor = TIER_COLORS[obj.confidence] ?? ESPRESSO;

  return (
    <aside
      role="dialog"
      aria-label="Sources for selected object"
      style={{
        position: 'fixed',
        right: 0,
        top: 0,
        bottom: 0,
        width: '360px',
        background: CREAM,
        color: ESPRESSO,
        fontFamily: SERIF_STACK,
        padding: '24px 20px',
        boxShadow: '-4px 0 12px rgba(0,0,0,0.08)',
        zIndex: 50,
        overflowY: 'auto',
      }}
    >
      <button
        onClick={() => setSelected(null)}
        aria-label="Close sources panel"
        style={{
          position: 'absolute',
          top: 12,
          right: 12,
          background: 'transparent',
          border: 'none',
          fontSize: '1.25rem',
          cursor: 'pointer',
          color: ESPRESSO,
          fontFamily: SERIF_STACK,
        }}
      >
        ×
      </button>
      <h2
        style={{
          fontSize: '1.4rem',
          fontWeight: 400,
          letterSpacing: '0.02em',
          marginTop: 0,
          marginBottom: 8,
        }}
      >
        {obj.id}
      </h2>
      <span
        className={`tier tier-${obj.confidence}`}
        style={{
          display: 'inline-block',
          padding: '2px 10px',
          border: `1px solid ${tierColor}`,
          color: tierColor,
          fontSize: '0.75rem',
          textTransform: 'uppercase',
          letterSpacing: '0.1em',
          marginBottom: 16,
        }}
      >
        {obj.confidence}
      </span>

      {resolved.length === 0 ? (
        <p style={{ fontStyle: 'italic', opacity: 0.7, marginTop: 12 }}>
          No source citations recorded for this object. Phase 6 content fill
          will add archival citations.
        </p>
      ) : (
        resolved.map((rec) => (
          <article
            key={rec.id}
            style={{
              marginTop: 20,
              paddingTop: 16,
              borderTop: '1px solid rgba(59,47,47,0.2)',
            }}
          >
            <h3 style={{ fontSize: '1rem', fontWeight: 500, margin: '0 0 4px' }}>
              {rec.title}
            </h3>
            <p
              style={{
                fontSize: '0.8rem',
                opacity: 0.75,
                margin: '0 0 8px',
              }}
            >
              {rec.attribution} · {rec.date}
            </p>
            {rec.how_we_know && (
              <p
                style={{
                  fontSize: '0.9rem',
                  lineHeight: 1.5,
                  margin: '8px 0',
                }}
              >
                {rec.how_we_know}
              </p>
            )}
            <p style={{ fontSize: '0.8rem', margin: '8px 0 4px' }}>
              <a
                href={rec.urls.live}
                target="_blank"
                rel="noreferrer"
                style={{ color: '#6B7C4E' }}
              >
                Live
              </a>
              {rec.urls.wayback && (
                <>
                  {' · '}
                  <a
                    href={rec.urls.wayback}
                    target="_blank"
                    rel="noreferrer"
                    style={{ color: '#6B7C4E' }}
                  >
                    Wayback
                  </a>
                </>
              )}
              {rec.urls.perma_cc && (
                <>
                  {' · '}
                  <a
                    href={rec.urls.perma_cc}
                    target="_blank"
                    rel="noreferrer"
                    style={{ color: '#6B7C4E' }}
                  >
                    Perma.cc
                  </a>
                </>
              )}
            </p>
            <p style={{ fontSize: '0.75rem', opacity: 0.65, marginTop: 4 }}>
              {rec.rights.statement_label}
            </p>
          </article>
        ))
      )}
    </aside>
  );
}
